import React, { createContext, useState, useContext } from 'react';
import {
  getAllCrew,
  getCrewById,
  createCrew,
  updateCrew,
  deleteCrew,
  publishCrew
} from '../services/crewService';

const CrewContext = createContext(null);

export const CrewProvider = ({ children }) => {
  const [crewMembers, setCrewMembers] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Fetch all crew members
   */
    const fetchAllCrew = async ({ page = 1, limit = 50, append = false } = {}) => {
    setLoading(true);
  setError(null);

  const response = await getAllCrew({ page, limit });

  if (response.success) {
    const newData = response.data || [];

    setCrewMembers(prev =>
      append ? [...prev, ...newData] : newData
    );

    setLoading(false);
    return newData;
  } else {
    setError(response.error);
    console.error("Failed to fetch crew members:", response.error);

    setLoading(false);
    return [];
  }
};


  /**
   * Fetch crew member by ID
   * @param {string|number} crewMemberId
   */
  const fetchCrewById = async (crewMemberId) => {
    setLoading(true);
    setError(null);

    const response = await getCrewById(crewMemberId);

    if (response.success) {
      setSelectedCrew(response.data);
    } else {
      setError(response.error);
      console.error('Failed to fetch crew member:', response.error);
    }

    setLoading(false);
    return response;
  };

  /**
   * Create new crew member with optimistic update
   * @param {FormData} formData
   */
  const handleCreateCrew = async (formData) => {
    setLoading(true);
    setError(null);

    const response = await createCrew(formData);

    if (response.success) {
      // Optimistic update: Add new crew member to list
      setCrewMembers((prev) => [...prev, response.data]);
    } else {
      setError(response.error);
      console.error('Failed to create crew member:', response.error);
    }

    setLoading(false);
    return response;
  };

  /**
   * Update existing crew member with optimistic update
   * @param {string|number} crewMemberId
   * @param {FormData|Object} data
   */
  const handleUpdateCrew = async (crewMemberId, data) => {
    setLoading(true);
    setError(null);

    // Store previous state for rollback on error
    const previousCrewMembers = [...crewMembers];
    const previousSelectedCrew = selectedCrew;

    // Optimistic update: Update crew member in list
    setCrewMembers((prev) =>
      prev.map((crew) =>
        crew.crew_member_id === crewMemberId
          ? { ...crew, ...(data instanceof FormData ? {} : data) }
          : crew
      )
    );

    // Update selected crew if it's the one being updated
    if (selectedCrew?.crew_member_id === crewMemberId) {
      setSelectedCrew((prev) => ({ ...prev, ...(data instanceof FormData ? {} : data) }));
    }

    const response = await updateCrew(crewMemberId, data);

    if (response.success) {
      // Update with actual response data
      setCrewMembers((prev) =>
        prev.map((crew) =>
          crew.crew_member_id === crewMemberId ? response.data : crew
        )
      );

      if (selectedCrew?.crew_member_id === crewMemberId) {
        setSelectedCrew(response.data);
      }
    } else {
      // Rollback optimistic update on error
      setCrewMembers(previousCrewMembers);
      setSelectedCrew(previousSelectedCrew);
      setError(response.error);
      console.error('Failed to update crew member:', response.error);
    }

    setLoading(false);
    return response;
  };


 const handlePublishCrew = async (crewMemberId) => {
  setLoading(true);
  setError(null);

  try {
    const response = await publishCrew(crewMemberId);

    if (response.success) {
      setCrewMembers((prev) =>
        prev.map((crew) =>
          crew.crew_member_id === crewMemberId ? { ...crew, is_draft: 0 } : crew
        )
      );
    } else {
      setError(response.error);
    }
    setLoading(false);
    return response;
  } catch (err) {
    setError("Failed to approve crew member");
    setLoading(false);
    return { success: false };
  }
};

  /**
   * Delete crew member with optimistic update
   * @param {string|number} crewMemberId
   */
  const handleDeleteCrew = async (crewMemberId) => {
    setLoading(true);
    setError(null);

    // Store previous state for rollback on error
    const previousCrewMembers = [...crewMembers];

    // Optimistic update: Remove crew member from list
    setCrewMembers((prev) =>
      prev.filter((crew) => crew.crew_member_id !== crewMemberId)
    );

    // Clear selected crew if it's the one being deleted
    if (selectedCrew?.crew_member_id === crewMemberId) {
      setSelectedCrew(null);
    }

    const response = await deleteCrew(crewMemberId);

    if (!response.success) {
      // Rollback optimistic update on error
      setCrewMembers(previousCrewMembers);
      setError(response.error);
      console.error('Failed to delete crew member:', response.error);
    }

    setLoading(false);
    return response;
  };

  /**
   * Clear selected crew member
   */
  const clearSelectedCrew = () => {
    setSelectedCrew(null);
  };

  const value = {
    // State
    crewMembers,
    selectedCrew,
    loading,
    error,

    // Methods
    fetchAllCrew,
    fetchCrewById,
    createCrew: handleCreateCrew,
    updateCrew: handleUpdateCrew,
    deleteCrew: handleDeleteCrew,
    publishCrew: handlePublishCrew,
    setSelectedCrew,
    clearSelectedCrew,
  };

  return <CrewContext.Provider value={value}>{children}</CrewContext.Provider>;
};

export const useCrew = () => {
  const context = useContext(CrewContext);
  if (!context) {
    throw new Error('useCrew must be used within CrewProvider');
  }
  return context;
};
